// ponytail: placeholder customer-impact data for the Customers tab (Customer Success view).
// Real sources later: module status from the Health tab, affected customers from Sentry issues joined
// to profiles/projects, deliveries from Communicore, shipped/coming from Linear.

export type ModuleState = 'ok' | 'degraded' | 'down';

export interface ModuleStatus {
    module: string;
    state: ModuleState;
    summary: string;
    /** Ready-to-send sentence for customers; only set when something is wrong. */
    customerMessage?: string;
}

export interface AffectedCustomer {
    customer: string;
    module: string;
    problem: string;
    projects: number;
    users: number;
    severity: 'high' | 'medium' | 'low';
    since: Date;
}

export interface DeliveryFailure {
    customer: string;
    channel: 'Email' | 'SMS';
    reason: string;
    count: number;
    lastAt: Date;
}

const minutesAgo = (m: number) => new Date(Date.now() - m * 60_000);
const hoursAgo = (h: number) => minutesAgo(h * 60);

export const moduleStatuses: ModuleStatus[] = [
    {
        module: 'Change Management',
        state: 'degraded',
        summary: 'Project overview fails to load for some projects.',
        customerMessage:
            "We're aware that the project overview in Change Management isn't loading for some projects. A fix is being tested and we expect it to be live today.",
    },
    {
        module: 'Sales',
        state: 'degraded',
        summary: 'Unit search is slow on large projects.',
        customerMessage:
            'Searching for units in Sales is slower than normal on large projects right now. Everything still works, and we are rolling out a fix.',
    },
    { module: 'Aftermarket', state: 'ok', summary: 'Working normally.' },
    { module: 'Quality Management', state: 'ok', summary: 'Working normally.' },
    { module: 'Flatfinder', state: 'ok', summary: 'Working normally.' },
    { module: 'Surveys', state: 'ok', summary: 'Working normally.' },
];

export const affectedCustomers: AffectedCustomer[] = [
    {
        customer: 'Nordbygg AS',
        module: 'Change Management',
        problem: 'Project overview shows an error',
        projects: 3,
        users: 22,
        severity: 'high',
        since: minutesAgo(40),
    },
    {
        customer: 'Fjordhus Eiendom',
        module: 'Sales',
        problem: 'Unit search takes 10+ seconds',
        projects: 2,
        users: 14,
        severity: 'medium',
        since: hoursAgo(2),
    },
    {
        customer: 'Bakketun Utvikling',
        module: 'Sales',
        problem: 'Unit search takes 10+ seconds',
        projects: 1,
        users: 6,
        severity: 'medium',
        since: hoursAgo(2),
    },
    {
        customer: 'Solvik Boligpartner',
        module: 'Change Management',
        problem: 'Project overview shows an error',
        projects: 1,
        users: 4,
        severity: 'high',
        since: minutesAgo(25),
    },
    {
        customer: 'Havnelys Bolig',
        module: 'Communication',
        problem: 'Buyer invitation emails bouncing',
        projects: 1,
        users: 31,
        severity: 'medium',
        since: hoursAgo(5),
    },
];

export const deliveries = {
    email: { sent: 4812, failed: 63 },
    sms: { sent: 1290, failed: 18 },
};

export const deliveryFailures: DeliveryFailure[] = [
    {
        customer: 'Havnelys Bolig',
        channel: 'Email',
        reason: 'Bounced: mailbox does not exist',
        count: 31,
        lastAt: minutesAgo(12),
    },
    {
        customer: 'Nordbygg AS',
        channel: 'Email',
        reason: 'Rejected as spam by recipient server',
        count: 17,
        lastAt: hoursAgo(1),
    },
    { customer: 'Fjordhus Eiendom', channel: 'SMS', reason: 'Invalid phone number', count: 11, lastAt: hoursAgo(3) },
    { customer: 'Bakketun Utvikling', channel: 'Email', reason: 'Bounced: mailbox full', count: 9, lastAt: hoursAgo(4) },
    { customer: 'Solvik Boligpartner', channel: 'SMS', reason: 'Carrier rejected message', count: 7, lastAt: hoursAgo(6) },
];
